import { useEffect, useRef } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';

import { useSellerTrackingProperties } from '../../../hooks/useSellerTrackingProperties';

// Wall-clock cap so an abandoned open tab doesn't record a multi-hour view.
// Product confirmed (1 Sep): 60s cap, wall-clock, and a tab-away counts as the
// end of the view (we fire on visibilitychange → hidden below).
const MAX_VIEW_DURATION_SECONDS = 60;

/**
 * Tracks how long the What's New pop-out is viewed before it's dismissed.
 *
 * The view ends on the first of: modal close, the tab being hidden
 * (visibilitychange → hidden), or pagehide — so tab-close / navigate-away still
 * emit rather than dropping the event. Fires at most once per open, reporting
 * min(elapsed, cap) seconds. Amplitude's browser SDK flushes on pagehide via
 * beacon, so the unload-path event survives.
 */
export const useTrackWhatsNewViewDuration = (isOpen: boolean) => {
  const sellerTrackingProperties = useSellerTrackingProperties();
  // Read the latest props at fire time without re-running the view timer (which
  // would spuriously end the view if the props resolve mid-view). The ref is
  // updated in an effect, not during render.
  const propertiesRef = useRef(sellerTrackingProperties);
  useEffect(() => {
    propertiesRef.current = sellerTrackingProperties;
  });

  useEffect(() => {
    if (!isOpen) return;

    const startedAt = performance.now();
    let hasFired = false;

    const fire = () => {
      if (hasFired) return;
      hasFired = true;
      const elapsedSeconds = (performance.now() - startedAt) / 1000;
      tracking.ampli.whatsNewModalClosed({
        view_duration_seconds: Math.round(
          Math.min(elapsedSeconds, MAX_VIEW_DURATION_SECONDS),
        ),
        ...propertiesRef.current,
      });
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') fire();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', fire);

    // Cleanup runs when the modal closes (isOpen → false) or on unmount, which
    // is the explicit-close path.
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', fire);
      fire();
    };
  }, [isOpen]);
};
